import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Loader2, History, Clock } from "lucide-react";

const Metric = ({ label, value, sub }) => (
  <div className="rounded-xl border border-white/8 bg-black/40 p-3 text-center">
    <p className="font-head text-2xl font-black text-white">{value}</p>
    <p className="font-tac text-[8px] tracking-widest text-white/40">{label}</p>
    {sub && <p className="mt-0.5 text-[10px] text-white/35">{sub}</p>}
  </div>
);

export const OctonStats = ({ teamId, oppId }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!teamId || !oppId || Number(teamId) === Number(oppId)) { setD(null); return; }
    let off = false;
    setLoading(true);
    api.get(`/octon-stats?team_id=${Number(teamId)}&opponent_id=${Number(oppId)}`)
      .then((r) => { if (!off) setD(r.data && !r.data.unavailable ? r.data : null); })
      .catch(() => { if (!off) setD(null); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [teamId, oppId]);

  if (!teamId || !oppId || Number(teamId) === Number(oppId)) return null;
  if (!loading && !d) return null;

  const h = d?.h2h;
  const gw = d?.goals_by_window;
  const maxW = gw ? Math.max(...Object.values(gw.windows)) : 1;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="card-tac mt-4 p-5 sm:p-6" data-testid="octon-stats"
      data-octon-section="octon stats head to head h2h trends goal timing" data-octon-label="OCTON Stats"
      data-octon-state={loading ? "loading" : "ready"}>
      <div className="mb-4 flex items-center gap-2">
        <History size={15} className="txt-cyan" />
        <span className="font-tac text-[10px] tracking-[0.3em] text-white/60">OCTON STATS · HEAD-TO-HEAD READ</span>
      </div>

      {loading && (
        <p className="flex items-center gap-2 py-6 text-sm text-white/50" data-testid="octon-stats-loading">
          <Loader2 size={15} className="animate-spin txt-cyan" /> Mining the head-to-head record…
        </p>
      )}

      {d && !loading && (
        <div className="space-y-5" data-testid="octon-stats-content">
          {h && (
            <div>
              <p className="mb-2 font-tac text-[9px] tracking-widest text-white/40">LAST {h.played} MEETINGS</p>
              <div className="flex h-9 w-full overflow-hidden rounded-lg text-[11px] font-bold">
                <div style={{ width: `${(h.home_wins / h.played) * 100}%` }} className="flex items-center justify-center bg-[#33d17a] text-black" data-testid="h2h-home-wins" title={`${d.home_name} wins`}>{h.home_wins}</div>
                <div style={{ width: `${(h.draws / h.played) * 100}%` }} className="flex items-center justify-center bg-white/15 text-white" title="Draws">{h.draws}</div>
                <div style={{ width: `${(h.away_wins / h.played) * 100}%` }} className="flex items-center justify-center bg-[#ff5a5a] text-black" data-testid="h2h-away-wins" title={`${d.away_name} wins`}>{h.away_wins}</div>
              </div>
              <div className="mt-1 flex justify-between text-[10px] text-white/45">
                <span>{d.home_name} {h.home_wins}W</span>
                <span>{h.draws}D · {h.home_goals}–{h.away_goals} goals</span>
                <span>{h.away_wins}W {d.away_name}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {h && <Metric label="AVG GOALS" value={h.avg_goals} sub="per meeting" />}
            {d.over_2_5 && <Metric label="OVER 2.5" value={`${d.over_2_5.pct}%`} sub={`${d.over_2_5.hit}/${d.over_2_5.of}`} />}
            {d.btts && <Metric label="BTTS" value={`${d.btts.pct}%`} sub={`${d.btts.hit}/${d.btts.of}`} />}
            {d.clean_sheets && <Metric label="CLEAN SHEETS" value={`${d.clean_sheets.home_pct}%`} sub={`${d.home_name?.split(" ").pop()}`} />}
          </div>

          {gw && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 font-tac text-[9px] tracking-widest text-white/40">
                <Clock size={11} /> WHEN GOALS COME · PEAK {gw.peak.window}′ ({gw.peak.pct}%)
              </p>
              <div className="flex items-end gap-1.5" style={{ height: 64 }}>
                {Object.entries(gw.windows).map(([w, pct]) => (
                  <div key={w} className="flex flex-1 flex-col items-center justify-end gap-1">
                    <div className="w-full rounded-t" title={`${pct}%`}
                      style={{ height: `${Math.max(4, (pct / maxW) * 48)}px`,
                        background: w === gw.peak.window ? "#00e5ff" : "rgba(255,255,255,0.16)" }} />
                    <span className="font-tac text-[7px] tracking-wide text-white/35">{w}</span>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-white/30">across {gw.goals_sampled} H2H goals · blended into the prediction</p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};
